<template>
  <slot></slot>
</template>

<script setup>
import { onBeforeUnmount, onMounted } from "vue";
import { useHostElement } from "#ce/composables/useHostElement";
import { MOUNT_READY_MARKER } from "#ce/framework/mount-ready";
import { toBoolean } from "#ce/utils/props";
import { props as guardProps } from "./props.js";

const props = defineProps(guardProps);
const host = useHostElement();

const SEPARATOR = "\u0000";
const UNLOCK_LINK_SELECTOR = '[data-behaviour~="enable-input"][data-canonical-unlock][data-target]';

let form = null;
let control = null;
let warning = null;
let observer = null;
let mirror = null;
let syncScheduled = false;
// Whether the guard switched the control on. Only then does it switch the control back off.
let autoChecked = false;
const savedFieldValues = new Map();
const savedWatchedIds = new Map();

function splitSelectors(value) {
  return String(value ?? "")
    .split(",")
    .map((selector) => selector.trim())
    .filter(Boolean);
}

function queryAll(selector) {
  try {
    return Array.from(form.querySelectorAll(selector));
  } catch {
    return [];
  }
}

function fieldValue(field) {
  if (field.type === "checkbox" || field.type === "radio") {
    return String(field.checked);
  }

  // A native select lists its selection in `selectedOptions`; an `sp-select` has none and holds it as an array in `value`.
  if (field.selectedOptions && field.multiple) {
    return joinValues(Array.from(field.selectedOptions, (option) => option.value));
  }

  if (Array.isArray(field.value)) {
    return joinValues(field.value);
  }

  return field.value ?? "";
}

function joinValues(values) {
  return values.map(String).sort().join(SEPARATOR);
}

function trackedFields() {
  const unlockTargets = toBoolean(props.ignoreUnlocked)
    ? []
    : queryAll(UNLOCK_LINK_SELECTOR).map((link) => link.dataset.target);
  const selectors = [...unlockTargets, ...splitSelectors(props.fields)];

  return Array.from(new Set(selectors.flatMap(queryAll)));
}

// An item is keyed by its own id, else by the id of its closest identified ancestor short of the
// form (an assignment list item sits in a container carrying the record's dom id), and only
// then by its text: a widget inside an item changes its text without an edit.
function watchedKey(item) {
  const owner = item.closest("[id]");

  if (owner && owner !== form) {
    return owner.id;
  }

  return item.textContent.trim();
}

function watchedIds(selector) {
  return queryAll(selector).map(watchedKey).sort().join(SEPARATOR);
}

// An `sp-*` field that has not mounted yet holds no value, so it is compared against its `defaultValue` instead.
function snapshot() {
  savedFieldValues.clear();
  for (const field of trackedFields()) {
    if (!field.localName.startsWith("sp-") || field.hasAttribute(MOUNT_READY_MARKER)) {
      savedFieldValues.set(field, fieldValue(field));
    }
  }

  savedWatchedIds.clear();
  for (const selector of splitSelectors(props.watch)) {
    savedWatchedIds.set(selector, watchedIds(selector));
  }
}

// The value a field was rendered with, which a user's edit does not touch: `defaultChecked`
// for a checkbox or radio, the options marked selected for a select (the first enabled option
// when a single select marks none, as the browser selects it), else `defaultValue`.
function defaultFieldValue(field) {
  if (field.type === "checkbox" || field.type === "radio") {
    return String(field.defaultChecked);
  }

  if (field.options) {
    const options = Array.from(field.options);
    const defaults = options.filter((option) => option.defaultSelected).map((option) => option.value);
    if (!field.multiple && defaults.length === 0) {
      return options.find((option) => !option.disabled)?.value ?? "";
    }

    return defaults.sort().join(SEPARATOR);
  }

  if (Array.isArray(field.defaultValue)) {
    return joinValues(field.defaultValue);
  }

  return field.defaultValue ?? "";
}

// A field added after mount (a new external id row) is compared against the value it was
// rendered with, so a row's defaults never count.
function fieldChanged(field) {
  const saved = savedFieldValues.get(field);

  return fieldValue(field) !== (saved === undefined ? defaultFieldValue(field) : saved);
}

function isDirty() {
  if (trackedFields().some(fieldChanged)) {
    return true;
  }

  for (const [selector, ids] of savedWatchedIds) {
    if (watchedIds(selector) !== ids) {
      return true;
    }
  }

  return false;
}

// Attributes are written only when they change: the observer reports every write, changed or not.
function setControlChecked(checked) {
  if (!control || control.checked === checked) {
    return;
  }

  control.checked = checked;
  control.dispatchEvent(new Event("change", { bubbles: true }));
}

// The control is disabled while a field differs: the record is claimed on save whatever the
// checkbox says, so the checkbox must not suggest otherwise. A disabled checkbox is left out
// of the form data, so a hidden mirror keeps a checked control's value submitted. The mirror
// goes before the control: the toggle-switch styles select the slider as the control's next
// sibling, so an element inserted after the control renders the switch as off.
function setControlDisabled(disabled) {
  if (!control || control.disabled === disabled) {
    return;
  }

  control.disabled = disabled;
  mirror?.remove();
  mirror = null;
  if (disabled && control.checked && control.name) {
    mirror = document.createElement("input");
    mirror.type = "hidden";
    mirror.name = control.name;
    mirror.value = control.value;
    control.insertAdjacentElement("beforebegin", mirror);
  }
}

function sync() {
  syncScheduled = false;
  const dirty = isDirty();

  if (warning && warning.hidden === dirty) {
    warning.hidden = !dirty;
  }

  if (dirty) {
    if (control && !control.checked) {
      autoChecked = true;
      setControlChecked(true);
    }
  } else if (autoChecked) {
    autoChecked = false;
    setControlChecked(false);
  }
  setControlDisabled(dirty);
}

function scheduleSync() {
  if (syncScheduled) {
    return;
  }

  syncScheduled = true;
  queueMicrotask(sync);
}

function handleFormEvent(event) {
  if (event.target !== control) {
    scheduleSync();
  }
}

function resolve(selector) {
  if (!selector) {
    return null;
  }

  return form.querySelector(selector) ?? document.querySelector(selector);
}

function attachToForm() {
  form = props.form ? document.querySelector(props.form) : host?.closest("form");
  if (!form) {
    return;
  }

  control = resolve(props.control);
  warning = resolve(props.warning);
  snapshot();

  form.addEventListener("input", handleFormEvent);
  form.addEventListener("change", handleFormEvent);
  // Attributes are observed too: a row removed through the admin's nested-list controls is hidden
  // and its `_destroy` input set without an event.
  observer = new MutationObserver(scheduleSync);
  observer.observe(form, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["style", "hidden", "disabled"],
  });
}

function detachFromForm() {
  form?.removeEventListener("input", handleFormEvent);
  form?.removeEventListener("change", handleFormEvent);
  observer?.disconnect();
  mirror?.remove();
}

onMounted(attachToForm);
onBeforeUnmount(detachFromForm);
</script>

<style>
:host {
  display: contents;
}
</style>
